<template>
  <div class="new-conversation px-2 pt-2">
    <placeholder-rect v-if="isFetchingAppProfiles" full-width class="m-0" />
    <div v-else-if="showSearchBox" class="search-wrapper">
      <div class="search-input p-relative">
        <div class="input-prepend p-absolute h-100 d-flex align-items-center">
          <v-icon icon="search" size="1.5rem" />
        </div>
        <v-select
          multiple
          deselect-from-dropdown
          :placeholder="
            $t('components.messageThread.newConversation.searchChatPlaceHolder')
          "
          label="name"
          :value="selectedSubscribers"
          :filterable="false"
          :close-on-select="false"
          :clear-search-on-blur="() => false"
          :clear-search-on-select="false"
          :options="displayedSubscribers"
          :dropdown-should-open="dropdownShouldOpen"
          :selectable="option => !option.isHeading"
          @search:focus="handleFocus"
          @search:blur="handleBlur"
          @search="handleSearch"
          @input="handleSelected"
        >
          <template #option="item">
            <div v-if="item.isHeading" class="heading">{{ item.name }}</div>
            <div v-else class="d-flex align-items-center subscriber__result">
              <subscriber-avatar
                class="mr-1"
                x-small
                :subscriber="{ name: item.name, avatar_urls: item.avatar_urls }"
              />
              <div>
                <h4 class="subscriber__result-name my-0">{{ item.name }}</h4>
              </div>
              <input
                type="checkbox"
                :checked="isSelectedSubscriber(item.id)"
                class="item-checkbox ml-auto"
                @click.stop="e => handleSubscriberInput(item, e.target.checked)"
              />
            </div>
          </template>

          <template #list-header>
            <div
              v-if="selectedSubscribersCount > 0"
              class="header d-flex justify-content-between align-items-center mb-2"
            >
              <subscriber-avatars
                :subscribers="selectedSubscribers"
                :limit="5"
                x-small
              />

              <v-button
                small
                primary
                :is-loading="isStartingChat"
                class="start-chat__button"
                @click="startChat"
              >
                {{
                  $t("components.messageThread.newConversation.chatWith", {
                    selectedSubscribersCount: selectedSubscribersCount
                  })
                }}
                {{
                  selectedSubscribersCount > 1
                    ? $t("components.messageThread.newConversation.members")
                    : $t("components.messageThread.newConversation.member")
                }}
              </v-button>
            </div>
          </template>

          <template slot="no-options">
            <template v-if="isLoading">
              <div
                class="d-flex align-items-center justify-content-center w-100"
              >
                <v-loader />
              </div>
            </template>
            <template v-else>
              {{
                $t("components.messageThread.newConversation.memberNotFound")
              }}
            </template>
          </template>
        </v-select>

        <div class="d-flex p-absolute search-input__actions h-100 pr-2">
          <div v-if="isLoading" class="d-flex align-items-center">
            <v-loader thin />
          </div>
          <v-button
            v-if="selectedSubscribers.length"
            ghost
            round
            class="search-input__dismiss mr-n2"
            @click="clearSearch"
          >
            <v-icon icon="close" />
          </v-button>
        </div>
      </div>
    </div>
    <v-button
      v-else-if="appMessagingProfiles.length"
      :is-loading="isStartingChat"
      class="w-100"
      @click="startChatWithAdmin"
    >
      <v-icon icon="chat" class="mr-1" />
      {{
        $t("components.messageThread.newConversation.message", {
          appName: app.name
        })
      }}
    </v-button>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex"
import { isPresent } from "@/lib/utils"
import debounce from "@/lib/debounce"

import vSelect from "vue-select"

export default {
  components: {
    vSelect,
    PlaceholderRect: () => import("@/components/PlaceholderRect"),
    SubscriberAvatar: () => import("@/components/SubscriberAvatar"),
    SubscriberAvatars: () => import("@/components/SubscriberAvatars")
  },

  data() {
    return {
      isFetchingAppProfiles: false,
      isLoading: false,
      isFocused: false,
      isStartingChat: false,
      selectedSubscribers: [],
      subscribers: [],
      value: ""
    }
  },

  computed: {
    ...mapState("messages", ["appMessagingProfiles"]),
    ...mapGetters("messages", ["appMessagingProfilesBySearchTerm"]),
    ...mapGetters("auth", ["currentUser", "canMessageAll"]),
    ...mapGetters([
      "app",
      "memberToAdminMessagingEnabled",
      "memberToMemberMessagingEnabled"
    ]),

    selectedSubscribersCount() {
      return this.selectedSubscribers.length
    },

    displayedSubscribers() {
      const adminSubscribers =
        !this.canMessageAll && this.memberToAdminMessagingEnabled
          ? this.appMessagingProfilesByTerm
          : []

      if (this.subscribers.length) {
        return adminSubscribers
          .concat({
            isHeading: true,
            name: this.$t(
              "components.messageThread.newConversation.memberHeading"
            )
          }) // Just for display heading
          .concat(this.subscribers)
      }

      return adminSubscribers
    },

    appMessagingProfilesByTerm() {
      return this.appMessagingProfilesBySearchTerm(this.value).filter(
        p => p.user_id != this.currentUser.id
      )
    },

    showSearchBox() {
      return (
        this.memberToMemberMessagingEnabled ||
        this.canMessageAll ||
        (this.memberToAdminMessagingEnabled &&
          this.appMessagingProfiles.length > 1)
      )
    },

    shouldFetchMembers() {
      return this.memberToMemberMessagingEnabled || this.canMessageAll
    }
  },

  deactivated() {
    this.selectedSubscribers = []
  },

  mounted() {
    this.isFetchingAppProfiles = true

    this.fetchMessagingProfiles().finally(() => {
      this.isFetchingAppProfiles = false
    })
  },

  methods: {
    isPresent,

    ...mapActions("messages", [
      "resolveThread",
      "fetchSubscribers",
      "fetchMessagingProfiles"
    ]),

    handleFocus() {
      this.isFocused = true
      this.handleSearch("")
    },

    handleBlur() {
      this.isFocused = false
    },

    handleSearch(value) {
      this.value = value

      if (this.shouldFetchMembers) {
        this.isLoading = true

        debounce(this.fetchSubscribersByTerm, 600)
      }
    },

    fetchSubscribersByTerm() {
      this.isLoading = true

      this.fetchSubscribers({ term: this.value })
        .then(data => {
          this.subscribers = data.subscribers.filter(
            s => !this.appMessagingProfiles.map(a => a.id).includes(s.id)
          )
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    isSelectedSubscriber(subscriber_id) {
      return !!this.selectedSubscribers.find(s => s.id == subscriber_id)
    },

    handleSelected(subscribers) {
      this.selectedSubscribers = subscribers
    },

    clearSearch() {
      this.selectedSubscribers = []
    },

    handleSubscriberInput(subscriber, checked) {
      const existingSubscriber = this.selectedSubscribers.find(
        s => s.id == subscriber.id
      )

      if (existingSubscriber && !checked) {
        this.selectedSubscribers = this.selectedSubscribers.filter(
          s => s.id != subscriber.id
        )
      } else if (!existingSubscriber) {
        this.selectedSubscribers.push(subscriber)
      }
    },

    startChatWithAdmin() {
      if (this.appMessagingProfiles.length < 1) return

      const subscriberId = this.appMessagingProfiles[0].id
      this.isStartingChat = true

      this.resolveThread({
        subscriberIds: [subscriberId],
        router: this.$router
      }).finally(() => (this.isStartingChat = false))
    },

    startChat() {
      // Although the "users" query param is used, it refers to the `subscriber_id` not the `user_id`
      // Confusing, I know... but its more user friendly for those snooping in the URL
      if (this.selectedSubscribersCount < 1) return

      const selectedSubscriberIds = this.selectedSubscribers.map(s => s.id)

      this.isStartingChat = true

      this.resolveThread({
        subscriberIds: selectedSubscriberIds,
        router: this.$router
      }).finally(() => (this.isStartingChat = false))
    },

    dropdownShouldOpen() {
      return this.selectedSubscribers.length || this.isFocused
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";
@import "@/assets/styles/mixins";
@import "~vue-select/dist/vue-select.css";

$search-input--border-radius: 0.375rem;

.new-conversation {
  .heading {
    font-size: $font-size--small;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: $font-weight--semi-bold;
  }

  .header {
    background-color: $color-ui--grey-10;
    position: sticky;
    top: -$size--2;
    z-index: 15;
    padding: $size--3 $size--4;
    margin: 0 - $size--2;

    .start-chat__button {
      padding: $size--3 $size--4;
      font-size: 13px;
    }
  }
}

.subscriber__result {
  .subscriber__result-name {
    font-size: $size--4;
    line-height: $font-size--base;
    font-weight: $font-weight--bold;
  }
}

.search-input {
  @include block-link;

  position: relative;
  border-radius: $search-input--border-radius;

  .input-prepend {
    padding-left: $size--2;
    color: $color-ui--grey-60;
    z-index: 10;
  }

  .vs__no-options {
    padding: 1rem 0;
    margin: 0;
    font-size: 1rem;
    font-weight: 500;
    color: $color-ui--grey-60;
  }

  .vs__actions {
    padding: 0;
    margin-right: 0.5rem;

    .vs__open-indicator {
      display: none;
    }

    .vs__spinner {
      border-width: 3px;
    }
  }

  .vs__dropdown-toggle {
    min-height: $input--base-height - 4px;
    padding: 0 $size--8;
    border: none;

    .vs__search {
      margin-top: 0;

      &::placeholder {
        color: $color-ui--grey-60;
        font-size: $font-size--small;
      }
    }

    .vs__selected-options {
      align-items: center;
      flex-wrap: nowrap;
      overflow-y: hidden;
      overflow-x: scroll;

      @include hide-scroll-bars;

      .vs__selected {
        height: 28px;
        font-size: $font-size--small;
        font-weight: $font-weight--semi-bold;
        background: transparent;
        border: none;
        padding: $size--1;
        white-space: nowrap;
      }

      .vs__deselect {
        margin-left: $size--2;
      }
    }
  }

  .vs__dropdown-menu {
    padding: $size--2;
    background-color: $color-ui--white;
    margin-top: -1px;
    box-shadow: $box-shadow--medium--no-bleed;
    border: 2px solid var(--primary-brand-color-hex);
    border-top: 0;
    border-radius: $search-input--border-radius;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    color: $color-ui--dark-blue;
    width: calc(100% + 4px);
    left: -2px;
    max-height: calc(
      60vh - 120px
    ); // 50% of viewport height minus the height of the header and search box

    .vs__dropdown-option {
      padding: $size--3 $size--2;
      margin-bottom: 0;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .vs__dropdown-option--disabled {
      background-color: $color-ui--white;
      margin-bottom: 0;
      color: $color-ui--black;
      cursor: auto;
    }

    .vs__dropdown-option--highlight {
      background-color: $color-ui--grey-10;
      border-radius: 4px;
      color: $color-ui--dark-blue;
    }

    .item-checkbox {
      transform: scale(1.5);
      padding: 10px;
      accent-color: $color-ui--dark-blue;
      transition: transform 0.2s;
      cursor: pointer;

      &:hover {
        transform: scale(1.6);
      }
    }
  }
}

.search-input__actions {
  top: 0;
  right: 0;
  z-index: 10;

  .v-loader {
    width: $size--5;
    height: $size--5;
    color: $color-ui--grey-50;
  }
}
</style>
